import type { CalculationProduct, Products, InverterData, RangePrice, ServicePackageData } from "../../types/calculationProduct.ts";
import { ProductTypes, ReferenceUnit } from "$lib/constants";
import { useConfiguratorProductsStore } from "$lib/store/configurator.products.store.ts";
import type { ModuleData } from "$lib/products/modules.ts";
import type { BatteryData } from "$lib/products/batteries.ts";
import type { ProductsByGoal } from "../../types/product";
import { ProductType, type AppProduct, type ProductScalingRules } from "@shared/interfaces/product";
import { createLogger } from "../logger.ts";
import staticProductStore from "../../spc-platform/mockData/product_store_static.json";

const logger = createLogger("SpcProductAdapter");

/**
 * Adapts normalised AppProducts (ProductsByGoal) into the CalculationProduct
 * shape expected by spc-platform stores and calculation utilities.
 *
 * All spc-platform imports are concentrated here — no other application module
 * should import from src/spc-platform directly except through this adapter and
 * CostCalculationService.
 */
export class SpcProductAdapter {

    /**
     * Converts a ProductsByGoal map (keyed by goal) into the flat CalculationProduct
     * array that spc-platform stores expect.
     */
    buildProductStore(products: ProductsByGoal): Products {
        const productsStore: Products = [];

        Object.values(products).forEach((shopifyProducts: AppProduct[]) => {
            shopifyProducts.forEach((item: AppProduct) => {
                switch (item.type) {
                    case ProductType.SOLAR_PANEL:
                        productsStore.push(this.getSolarModuleProduct(item));
                        break;
                    case ProductType.INVERTER:
                        productsStore.push(this.getInverterProduct(item));
                        break;
                    case ProductType.BATTERY_STORAGE:
                        productsStore.push(this.getBatteryProduct(item));
                        break;
                    case ProductType.ACCESSORY:
                    case ProductType.MOUNTING_SYSTEM:
                    case ProductType.OTHERS:
                        productsStore.push(this.getGeneralProduct(item));
                        break;
                    case ProductType.SERVICE_PACKAGE:
                        productsStore.push(this.getServicePackageProduct(item));
                        break;
                    default:
                        logger.warn('Unknown product type', { type: item.type });
                        productsStore.push(this.getGeneralProduct(item));
                        break;
                }
            });
        });

        productsStore.push(...this.getStaticDefaultProducts());

        logger.debug('built product store', { productsStore });
        return productsStore;
    }

    /**
     * Loads the static mock product store and keeps only the products flagged
     * as `default: true`, so they can be merged into the built product store.
     */
    private getStaticDefaultProducts(): Products {
        return (staticProductStore as unknown as CalculationProduct[])
            .filter(product => product.default === true);
    }

    /**
     * Pushes the built product store into the spc-platform configurator store so
     * calculation utilities can access it.
     */
    initConfiguratorProductsStore(productsStore: Products): void {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useConfiguratorProductsStore(productsStore);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Scaling-rule helpers
    // ─────────────────────────────────────────────────────────────────────────

    private getCapacitiesFromProduct(product: AppProduct): number[] {
        if (!product.scalingRules) return [product.powerClass || 0];
        try {
            const rules = product.scalingRules as ProductScalingRules;
            return rules.scalingRules?.map(rule => rule.maxPeak) || [];
        } catch {
            logger.error('Error parsing scaling rules for product', { productId: product.id });
            return [];
        }
    }

    private getPricesFromProduct(product: AppProduct): number[] {
        if (!product.scalingRules) return [];
        try {
            const rules = product.scalingRules as ProductScalingRules;
            return rules.scalingRules?.map(rule => rule.price) || [];
        } catch {
            logger.error('Error parsing scaling rules for product', { productId: product.id });
            return [];
        }
    }

    private getRangePricesFromProduct(product: AppProduct): RangePrice[] {
        if (!product.scalingRules) return [];
        try {
            const rules = product.scalingRules as ProductScalingRules;
            return rules.scalingRules?.map(rule => ({
                min: rule.minPeak,
                max: rule.maxPeak,
                price: rule.price,
            } as RangePrice)) || [];
        } catch {
            logger.error('Error parsing scaling rules for product', { productId: product.id });
            return [];
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // CalculationProduct builders
    // ─────────────────────────────────────────────────────────────────────────

    private getSolarModuleProduct(product: AppProduct): CalculationProduct {
        return {
            ...this.getGeneralProduct(product),
            type: ProductTypes.MODULE,
            positionInProposal: "PV",
            data: {
                area: product.area,
                priceType: 0,
                moduleType: product.moduleType,
                powerClass: product.powerClass,
                subsidyType: 0,
                manufacturer: product.manufacturer,
                subsidyShare: 0,
                priceCategory: [0, 0, 0, 0, 0, 0, 0, 0, 0],
                subsidyAmount: 0,
                efficiencyFactor: product.efficiencyFactor,
                eligibleForSubsidy: false,
                usePreisCategory: false,
            } as ModuleData,
        };
    }

    private getInverterProduct(product: AppProduct): CalculationProduct {
        const rangePrices = this.getRangePricesFromProduct(product);
        return {
            ...this.getGeneralProduct(product),
            type: ProductTypes.INVESTER,
            positionInProposal: "WR",
            data: {
                usePreisCategory: rangePrices.length > 0,
                serialName: product.name,
                range: rangePrices,
            } as InverterData,
        };
    }
    private getBatteryProduct(product: AppProduct): CalculationProduct {
        const capacities = this.getCapacitiesFromProduct(product);
        const prices = this.getPricesFromProduct(product);
        return {
            ...this.getGeneralProduct(product),
            type: ProductTypes.BATTERY,
            positionInProposal: "BAT",
            price: prices.length > 0 ? 0 : product.price,
            data: {
                type: "AC",
                maxDCPower: 7,
                capacities,
                pricesWithoutInstallation: prices,
                pricesWithInstallation: prices,
                batteryPrice: product.price,
                manufacturer: product.manufacturer,
                maxBatteries: capacities.length,
                inverterPrice: 0,
                batteryCapacity: Math.max(...capacities),
                installationPrice: 0,
            } as BatteryData,
        };
    }

    private getServicePackageProduct(product: AppProduct): CalculationProduct {
        return {
            ...this.getGeneralProduct(product),
            data: {
                type: ProductType.SERVICE_PACKAGE,
                range: this.getRangePricesFromProduct(product),
            } as ServicePackageData,
        };
    }

    private getGeneralProduct(product: AppProduct): CalculationProduct {
        return {
            // @ts-expect-error Id is currently a string, but spc platform only accepts number
            id: product.id,
            name: product.name,
            data: {},
            type: ProductTypes.GENERAL,
            active: true,
            default: false,
            defaultQuantity: 0,
            archived: false,
            description: product.description,
            dataSheetId: 0,
            surchargePercentage: product.surchargePercentage || 0,
            referenceUnit: ReferenceUnit.UNIT,
            positionInProposal: "GEN",
            price: product.price,
            visible: true,
            tenantId: 1,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
    }
}
